import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LifeBuoy } from "lucide-react";
import type { SupportTicketDto } from "@finora/types";
import { Button, EmptyState, Skeleton, StatusBadge } from "@finora/ui";
import { apiFetch } from "../../lib/api";

const STATUS_LABELS: Record<string, string> = {
  OPEN: "Otvoren",
  IN_PROGRESS: "U obradi",
  RESOLVED: "Rešen",
  CLOSED: "Zatvoren",
};
const STATUS_TONE: Record<string, "success" | "error" | "neutral" | "warning"> = {
  OPEN: "warning",
  IN_PROGRESS: "neutral",
  RESOLVED: "success",
  CLOSED: "neutral",
};
const STATUS_FILTERS = ["", "OPEN", "IN_PROGRESS", "RESOLVED", "CLOSED"];

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString("sr-RS");
}

function TicketDetail({ id, onChanged }: { id: string; onChanged: () => void }) {
  const queryClient = useQueryClient();
  const [reply, setReply] = useState("");

  const { data: ticket, isLoading } = useQuery({
    queryKey: ["admin-support-ticket", id],
    queryFn: () => apiFetch<SupportTicketDto>(`/super-admin/support-tickets/${id}`),
  });

  const sendReply = useMutation({
    mutationFn: () =>
      apiFetch<SupportTicketDto>(`/super-admin/support-tickets/${id}/messages`, {
        method: "POST",
        body: JSON.stringify({ message: reply }),
      }),
    onSuccess: () => {
      setReply("");
      queryClient.invalidateQueries({ queryKey: ["admin-support-ticket", id] });
      onChanged();
    },
  });

  const changeStatus = useMutation({
    mutationFn: (status: string) =>
      apiFetch<SupportTicketDto>(`/super-admin/support-tickets/${id}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-support-ticket", id] });
      onChanged();
    },
  });

  if (isLoading || !ticket) return <Skeleton className="h-64 w-full" />;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">{ticket.subject}</h2>
          <p className="text-sm text-slate-500">
            {ticket.companyName ?? ticket.companyId} · otvoren {formatDateTime(ticket.createdAt)}
          </p>
        </div>
        <select
          value={ticket.status}
          onChange={(e) => changeStatus.mutate(e.target.value)}
          className="rounded-lg border border-border px-3 py-1.5 text-sm"
        >
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      <div className="max-h-96 space-y-3 overflow-auto rounded-xl border border-border bg-white p-4">
        {ticket.messages?.map((m) => (
          <div
            key={m.id}
            className={`rounded-lg p-3 text-sm ${
              m.authorType === "ADMIN" ? "bg-blue-50 text-blue-900" : "bg-slate-50 text-slate-800"
            }`}
          >
            <div className="mb-1 flex items-center justify-between text-xs text-slate-500">
              <span className="font-medium">
                {m.authorName ?? (m.authorType === "ADMIN" ? "HAFiskal podrška" : "Korisnik")}
              </span>
              <span>{formatDateTime(m.createdAt)}</span>
            </div>
            <p className="whitespace-pre-wrap">{m.message}</p>
          </div>
        ))}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (reply.trim()) sendReply.mutate();
        }}
        className="space-y-2"
      >
        <textarea
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          rows={3}
          placeholder="Odgovor kao HAFiskal podrška..."
          className="w-full rounded-lg border border-border px-3 py-2 text-sm"
        />
        <div className="flex justify-end">
          <Button type="submit" disabled={sendReply.isPending || !reply.trim()}>
            {sendReply.isPending ? "Šaljem..." : "Pošalji odgovor"}
          </Button>
        </div>
      </form>
    </div>
  );
}

export function SupportTicketsAdminPage() {
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin-support-tickets", statusFilter],
    queryFn: () =>
      apiFetch<SupportTicketDto[]>(
        `/super-admin/support-tickets${statusFilter ? `?status=${statusFilter}` : ""}`,
      ),
  });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Podrška - svi tiketi</h1>
        <p className="text-sm text-slate-500">Tiketi podrške svih firmi na platformi.</p>
      </div>

      <div className="flex gap-2">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s || "all"}
            onClick={() => setStatusFilter(s)}
            className={`rounded-lg px-3 py-1.5 text-sm ${
              statusFilter === s ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {s ? STATUS_LABELS[s] : "Svi"}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-border bg-surface shadow-subtle">
          {isLoading ? (
            <div className="space-y-2 p-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : !data || data.length === 0 ? (
            <EmptyState icon={LifeBuoy} title="Nema tiketa" description="" />
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-border text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3">Firma</th>
                  <th className="px-4 py-3">Naslov</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.map((t) => (
                  <tr
                    key={t.id}
                    className={`cursor-pointer border-b border-border last:border-0 hover:bg-slate-50 ${
                      selectedId === t.id ? "bg-blue-50" : ""
                    }`}
                    onClick={() => setSelectedId(t.id)}
                  >
                    <td className="px-4 py-3 text-slate-600">{t.companyName ?? t.companyId}</td>
                    <td className="px-4 py-3 font-medium text-slate-800">{t.subject}</td>
                    <td className="px-4 py-3">
                      <StatusBadge
                        label={STATUS_LABELS[t.status] ?? t.status}
                        tone={STATUS_TONE[t.status] ?? "neutral"}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
          {selectedId ? (
            <TicketDetail id={selectedId} onChanged={() => refetch()} />
          ) : (
            <p className="text-sm text-slate-500">Izaberite tiket sa liste da vidite razgovor.</p>
          )}
        </div>
      </div>
    </div>
  );
}
